/// <reference types="@types/google.maps" />

import { TourDetail } from '../types/tour';
import { buildGeocodeQuery, buildPlacesSearchQuery } from '../utils/tourLocation';

export interface TourCityContext {
  tourCity: string;
  bounds?: google.maps.LatLngBounds;
  center?: google.maps.LatLng;
  countryCode?: string;
}

const cityContextCache = new Map<string, TourCityContext>();

export function waitForGoogleMaps(timeoutMs = 10000): Promise<boolean> {
  if (window.google?.maps) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const start = Date.now();
    const interval = window.setInterval(() => {
      if (window.google?.maps) {
        window.clearInterval(interval);
        resolve(true);
        return;
      }

      if (Date.now() - start >= timeoutMs) {
        window.clearInterval(interval);
        resolve(false);
      }
    }, 100);
  });
}

function getMapsApiKey(): string | undefined {
  return process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
}

function extractCountryCode(result: google.maps.GeocoderResult): string | undefined {
  const country = result.address_components?.find((component) =>
    component.types.includes('country')
  );

  return country?.short_name?.toLowerCase();
}

export async function resolveTourCityContext(tourCity: string): Promise<TourCityContext> {
  const cacheKey = tourCity.trim().toLowerCase();
  const cached = cityContextCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const fallback: TourCityContext = { tourCity };

  if (!window.google?.maps) {
    return fallback;
  }

  const context = await new Promise<TourCityContext>((resolve) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: tourCity }, (results, status) => {
      if (status !== 'OK' || !results?.[0]) {
        resolve(fallback);
        return;
      }

      const result = results[0];
      resolve({
        tourCity,
        bounds: result.geometry.viewport || result.geometry.bounds,
        center: result.geometry.location,
        countryCode: extractCountryCode(result),
      });
    });
  });

  cityContextCache.set(cacheKey, context);
  return context;
}

async function searchPlaceWithPlacesApi(
  textQuery: string,
  cityContext: TourCityContext,
  apiKey: string
): Promise<{ lat: number; lng: number } | null> {
  const requestBody: Record<string, unknown> = { textQuery };

  if (cityContext.countryCode) {
    requestBody.regionCode = cityContext.countryCode.toUpperCase();
  }

  if (cityContext.center) {
    requestBody.locationBias = {
      circle: {
        center: {
          latitude: cityContext.center.lat(),
          longitude: cityContext.center.lng(),
        },
        radius: 50000,
      },
    };
  }

  try {
    const response = await fetch('https://places.googleapis.com/v1/places:searchText', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': apiKey,
        'X-Goog-FieldMask': 'places.location,places.displayName,places.formattedAddress,places.id',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json() as {
      places?: Array<{
        location?: { latitude?: number; longitude?: number };
      }>;
    };

    const place = data.places?.[0];
    if (!place?.location?.latitude || place.location.longitude === undefined) {
      return null;
    }

    return {
      lat: place.location.latitude,
      lng: place.location.longitude,
    };
  } catch {
    return null;
  }
}

async function geocodeWithBias(
  address: string,
  cityContext: TourCityContext
): Promise<{ lat: number; lng: number } | null> {
  if (!window.google?.maps || !address.trim()) {
    return null;
  }

  return new Promise((resolve) => {
    const geocoder = new google.maps.Geocoder();
    const request: google.maps.GeocoderRequest = { address };

    if (cityContext.bounds) {
      request.bounds = cityContext.bounds;
    }

    if (cityContext.countryCode) {
      request.componentRestrictions = { country: cityContext.countryCode };
    }

    geocoder.geocode(request, (results, status) => {
      if (status !== 'OK' || !results?.[0]) {
        resolve(null);
        return;
      }

      const location = results[0].geometry.location;
      resolve({ lat: location.lat(), lng: location.lng() });
    });
  });
}

export async function resolveStopCoordinates(
  detail: TourDetail,
  tourCity: string,
  cityContext: TourCityContext
): Promise<{ lat: number; lng: number } | null> {
  const apiKey = getMapsApiKey();
  if (!apiKey) {
    return geocodeWithBias(buildGeocodeQuery(detail, tourCity), cityContext);
  }

  const placesQuery = buildPlacesSearchQuery(detail, tourCity);
  const placesResult = await searchPlaceWithPlacesApi(placesQuery, cityContext, apiKey);
  if (placesResult) {
    return placesResult;
  }

  const geocodeQuery = buildGeocodeQuery(detail, tourCity);
  return geocodeWithBias(geocodeQuery, cityContext);
}
